import React, { useState, useEffect, useCallback } from 'react';
import axios from '../config/axios';
import '../assets/styles/AllowanceManager.css';

const AllowanceManager = ({ householdId }) => {
  const [allowances, setAllowances] = useState([]);
  const [kids, setKids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [editingAllowance, setEditingAllowance] = useState(null);

  const [formData, setFormData] = useState({
    kid_user_id: '',
    amount: '',
    frequency: 'weekly',
    day_of_week: '0',
    day_of_month: '1',
    start_date: new Date().toISOString().split('T')[0]
  });

  const fetchAllowances = useCallback(async () => {
    try {
      const response = await axios.get(`/api/v1/allowances?household_id=${householdId}`);
      setAllowances(response.data || []);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch allowances:', err);
      setError('Failed to load allowances');
    } finally {
      setLoading(false);
    }
  }, [householdId]);

  const fetchKids = useCallback(async () => {
    try {
      const response = await axios.get(`/api/v1/users/kids?tenant_id=${householdId}`);
      const normalizedKids = (response.data || []).map(kid => ({
        ...kid,
        id: kid.ID || kid.id,
        ID: kid.ID || kid.id,
        DisplayName: kid.DisplayName || kid.display_name || kid.Username || kid.username || '',
        Username: kid.Username || kid.username || '',
        Avatar: kid.Avatar || kid.avatar || '👶'
      }));
      setKids(normalizedKids);
    } catch (err) {
      console.error('Failed to fetch kids:', err);
    }
  }, [householdId]);

  useEffect(() => {
    fetchAllowances();
    fetchKids();
  }, [fetchAllowances, fetchKids]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = {
        kid_user_id: formData.kid_user_id,
        amount: parseInt(formData.amount) * 100, // Convert to cents
        frequency: formData.frequency,
        start_date: formData.start_date
      };

      if (formData.frequency === 'weekly' || formData.frequency === 'biweekly') {
        payload.day_of_week = parseInt(formData.day_of_week);
      } else if (formData.frequency === 'monthly') {
        payload.day_of_month = parseInt(formData.day_of_month);
      }

      if (editingAllowance) {
        await axios.put(`/api/v1/allowances?id=${editingAllowance.id}`, payload);
      } else {
        await axios.post('/api/v1/allowances', payload);
      }

      fetchAllowances();
      closeModal();
    } catch (err) {
      console.error('Failed to save allowance:', err);
      setError('Failed to save allowance');
    }
  };

  const handleEdit = (allowance) => {
    setEditingAllowance(allowance);
    setFormData({
      kid_user_id: allowance.kid_user_id,
      amount: (allowance.amount / 100).toString(),
      frequency: allowance.frequency,
      day_of_week: allowance.day_of_week?.toString() || '0',
      day_of_month: allowance.day_of_month?.toString() || '1',
      start_date: new Date().toISOString().split('T')[0]
    });
    setIsAddModalOpen(true);
  };

  const handleDelete = async (allowanceId) => {
    if (!window.confirm('Are you sure you want to delete this allowance?')) {
      return;
    }

    try {
      await axios.delete(`/api/v1/allowances?id=${allowanceId}`);
      fetchAllowances();
    } catch (err) {
      console.error('Failed to delete allowance:', err);
      setError('Failed to delete allowance');
    }
  };

  const toggleEnabled = async (allowance) => {
    try {
      await axios.put(
        `/api/v1/allowances?id=${allowance.id}`,
        { enabled: !allowance.enabled }
      );
      fetchAllowances();
    } catch (err) {
      console.error('Failed to toggle allowance:', err);
      setError('Failed to toggle allowance');
    }
  };

  const closeModal = () => {
    setIsAddModalOpen(false);
    setEditingAllowance(null);
    setFormData({
      kid_user_id: '',
      amount: '',
      frequency: 'weekly',
      day_of_week: '0',
      day_of_month: '1',
      start_date: new Date().toISOString().split('T')[0]
    });
  };

  const getKidName = (kidUserId) => {
    const kid = kids.find(k => k.id === kidUserId);
    return kid ? kid.display_name || kid.username : 'Unknown';
  };

  const formatFrequency = (frequency) => {
    return frequency.charAt(0).toUpperCase() + frequency.slice(1);
  };

  const getDayName = (dayOfWeek) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return days[dayOfWeek];
  };

  const formatNextPayment = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return <div className="allowance-manager-loading">Loading allowances...</div>;
  }

  return (
    <div className="allowance-manager">
      <div className="allowance-header">
        <h2>💰 Allowance Management</h2>
        <button className="btn-add-allowance" onClick={() => setIsAddModalOpen(true)} aria-label="Add new allowance">
          + Add Allowance
        </button>
      </div>

      {error && <div className="error-message" role="alert" aria-live="assertive">{error}</div>}

      {allowances.length === 0 ? (
        <div className="no-allowances">
          <p>No allowances configured yet.</p>
          <p>Set up recurring allowances to automatically pay your kids on a schedule!</p>
        </div>
      ) : (
        <div className="allowances-list">
          {allowances.map(allowance => (
            <div key={allowance.id} className={`allowance-card allowance-item ${!allowance.enabled ? 'disabled' : ''}`}>
              <div className="allowance-info">
                <div className="allowance-kid">
                  <span className="kid-name">{getKidName(allowance.kid_user_id)}</span>
                  <span className={`status-badge ${allowance.enabled ? 'active' : 'inactive'}`}>
                    {allowance.enabled ? 'Active' : 'Paused'}
                  </span>
                </div>
                <div className="allowance-details">
                  <div className="detail-item">
                    <span className="detail-label">Amount:</span>
                    <span className="detail-value allowance-amount">${(allowance.amount / 100).toFixed(2)} DB</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Frequency:</span>
                    <span className="detail-value allowance-frequency">
                      {formatFrequency(allowance.frequency)}
                      {allowance.day_of_week != null && ` (${getDayName(allowance.day_of_week)})`}
                      {allowance.day_of_month != null && ` (Day ${allowance.day_of_month})`}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Next Payment:</span>
                    <span className="detail-value">{formatNextPayment(allowance.next_payment_date)}</span>
                  </div>
                </div>
              </div>
              <div className="allowance-actions">
                <button
                  className="btn-toggle"
                  onClick={() => toggleEnabled(allowance)}
                  title={allowance.enabled ? 'Pause' : 'Resume'}
                  aria-label={allowance.enabled ? `Pause allowance for ${getKidName(allowance.kid_user_id)}` : `Resume allowance for ${getKidName(allowance.kid_user_id)}`}
                >
                  {allowance.enabled ? '⏸️' : '▶️'}
                </button>
                <button
                  className="btn-edit"
                  onClick={() => handleEdit(allowance)}
                  title="Edit"
                  aria-label={`Edit allowance for ${getKidName(allowance.kid_user_id)}`}
                >
                  ✏️
                </button>
                <button
                  className="btn-delete"
                  onClick={() => handleDelete(allowance.id)}
                  title="Delete"
                  aria-label={`Delete allowance for ${getKidName(allowance.kid_user_id)}`}
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {isAddModalOpen && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" role="dialog" aria-modal="true" aria-label={editingAllowance ? 'Edit Allowance' : 'Add New Allowance'} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingAllowance ? 'Edit Allowance' : 'Add New Allowance'}</h3>
              <button className="modal-close" onClick={closeModal} aria-label="Close modal">×</button>
            </div>
            <form onSubmit={handleSubmit} className="allowance-form">
              <div className="form-group">
                <label htmlFor="allowance-kid">Kid:</label>
                <select
                  id="allowance-kid"
                  name="kid_user_id"
                  value={formData.kid_user_id}
                  onChange={handleInputChange}
                  required
                  disabled={editingAllowance}
                >
                  <option value="">Select a kid</option>
                  {kids.map(kid => (
                    <option key={kid.id} value={kid.id}>
                      {kid.display_name || kid.username}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="allowance-amount">Amount (DB):</label>
                <input
                  id="allowance-amount"
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  min="0.01"
                  step="0.01"
                  required
                  placeholder="5.00"
                />
              </div>

              <div className="form-group">
                <label htmlFor="allowance-frequency">Frequency:</label>
                <select
                  id="allowance-frequency"
                  name="frequency"
                  value={formData.frequency}
                  onChange={handleInputChange}
                  required
                >
                  <option value="weekly">Weekly</option>
                  <option value="biweekly">Bi-weekly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              {(formData.frequency === 'weekly' || formData.frequency === 'biweekly') && (
                <div className="form-group">
                  <label>Day of Week:</label>
                  <select
                    name="day_of_week"
                    value={formData.day_of_week}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="0">Sunday</option>
                    <option value="1">Monday</option>
                    <option value="2">Tuesday</option>
                    <option value="3">Wednesday</option>
                    <option value="4">Thursday</option>
                    <option value="5">Friday</option>
                    <option value="6">Saturday</option>
                  </select>
                </div>
              )}

              {formData.frequency === 'monthly' && (
                <div className="form-group">
                  <label>Day of Month:</label>
                  <input
                    type="number"
                    name="day_of_month"
                    value={formData.day_of_month}
                    onChange={handleInputChange}
                    min="1"
                    max="31"
                    required
                  />
                </div>
              )}

              {!editingAllowance && (
                <div className="form-group">
                  <label>Start Date:</label>
                  <input
                    type="date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              )}

              <div className="form-actions">
                <button type="button" className="btn-cancel" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-submit">
                  {editingAllowance ? 'Update' : 'Create'} Allowance
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllowanceManager;
